import { useMutation } from '@tanstack/react-query';
import React from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';

import { bulkDeleteItems } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Category, MenuContainer } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { ConfirmModal } from 'common/components';
import { reportBulkAction } from 'modules/menu/behavioral';
import { useBulkDeleteTracking } from 'modules/menu/hooks/telemetryHooks';
import useMenuData from 'modules/menu/useMenuData';

import { buildBulkActionBasePayload } from '../../utils';
import BulkActionModalTitle from '../BulkActionModalTitle';

type Props = {
  categories: Category[];
  menuId: string;
  onClose: () => void;
  onClearSelection: () => void;
  selectedIds: Set<string>;
};

const BulkDeleteModal = ({ categories, menuId, onClose, onClearSelection, selectedIds }: Props) => {
  const { updateMenuData } = useMenuData();
  const trackDeleteItems = useBulkDeleteTracking([...selectedIds]);

  const handleBulkDelete = useMutation(
    () => bulkDeleteItems(menuId, buildBulkActionBasePayload(categories, selectedIds)),
    {
      onMutate: () => {
        reportBulkAction(selectedIds.size, 'BULK_DELETE_ITEMS');
      },
      onSuccess: (data: MenuContainer) => {
        updateMenuData({ actionType: 'updateMenuContainer', payload: data });
        onClearSelection();
        trackDeleteItems();
        toast.success(<FormattedMessage id="menu.bulkActions.deleteItemsSuccessDescription" />);
      },
      onError: () => {
        toast.error(<FormattedMessage id="menu.bulkActions.deleteItemsErrorDescription" />, { duration: Infinity });
      },
      onSettled: () => {
        onClose();
      },
    },
  );

  return (
    <ConfirmModal
      cancelText={<FormattedMessage id="state.cancel" />}
      confirmText={<FormattedMessage id="menu.bulkActions.deleteItems" />}
      confirmVariant={'lightRed'}
      description={<FormattedMessage id="menu.bulkActions.deleteItemsDescription" />}
      header={
        <BulkActionModalTitle count={selectedIds.size} title={<FormattedMessage id="menu.bulkActions.deleteItems" />} />
      }
      loading={handleBulkDelete.isLoading}
      onCancel={onClose}
      onConfirm={() => {
        handleBulkDelete.mutate();
      }}
      open
    />
  );
};

export default BulkDeleteModal;
